import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Paginator from '../../components/ui/Paginator';
import Message from '../../components/ui/Message';
import { costingApi, formatCost, formatMoney, formatQuantity } from './costing';

const PAGE_SIZE = 15;

// Compras registradas: detalle de cada una y anulación (revierte el inventario con el costo de la compra)
export const PurchasesPage = () => {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [data, setData] = useState<any>({ purchases: [], totalPages: 0, totalRecords: 0 });
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState<{ purchase: any; lines: any[] } | null>(null);
    const [cancelling, setCancelling] = useState<any | null>(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const load = async (p: number) => {
        setLoading(true);
        const resp = await costingApi.purchases(p, PAGE_SIZE);
        setLoading(false);
        if (resp.data) setData(resp.data);
        else setError(resp.error!);
    };

    useEffect(() => { load(page); }, [page]);

    const openDetail = async (purchase: any) => {
        const resp = await costingApi.purchase(purchase.id);
        if (resp.data) setDetail({ purchase, lines: resp.data.lines });
        else setError(resp.error!);
    };

    const cancel = async (reason?: string) => {
        const purchase = cancelling;
        setCancelling(null);
        const resp = await costingApi.cancelPurchase(purchase.id, reason ?? '');
        if (resp.error) {
            setError(resp.error);
            return;
        }
        setSuccess(`Se anuló la compra ${purchase.id} y se revirtió su inventario.`);
        load(page);
    };

    return (
        <div className='mt-5 container-fluid px-4' style={{ flex: 1, marginTop: 10 }}>
            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Compras</h3>
                    <p className="text-muted mb-0">Compras a proveedores con su costo y "Otros costos".</p>
                </div>
                <div className="d-flex gap-2 mt-2 mt-md-0">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/stocks')}>
                        <i className="bi bi-arrow-left me-1" /> Volver al inventario
                    </button>
                    <button type="button" className="btn btn-primary" onClick={() => navigate('/stock-purchase')}>
                        <i className="bi bi-plus-lg me-1" /> Nueva compra
                    </button>
                </div>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>N.º</th>
                                    <th>Fecha</th>
                                    <th>Proveedor</th>
                                    <th>Factura</th>
                                    <th className="text-end">Otros costos</th>
                                    <th className="text-end">Total</th>
                                    <th>Estado</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={8} className="text-center py-4"><span className="spinner-border spinner-border-sm me-2" /> Cargando...</td></tr>
                                ) : data.purchases.length === 0 ? (
                                    <tr><td colSpan={8} className="text-center py-4 text-muted">No hay compras registradas.</td></tr>
                                ) : data.purchases.map((p: any) => (
                                    <tr key={p.id}>
                                        <td>{p.id}</td>
                                        <td className="small text-nowrap">{p.date ? new Date(p.date).toLocaleString() : '-'}</td>
                                        <td>{p.supplierName ?? '-'}</td>
                                        <td>{p.supplierInvoice ?? '-'}</td>
                                        <td className="text-end">{formatMoney(p.otherCosts, p.currency)}</td>
                                        <td className="text-end fw-semibold">{formatMoney(p.total)}</td>
                                        <td>
                                            <span className={`badge rounded-pill ${p.status === 'ANULADA' ? 'bg-secondary-subtle text-secondary-emphasis' : 'bg-success-subtle text-success-emphasis'}`}
                                                title={p.cancelReason ?? undefined}>
                                                {p.status === 'ANULADA' ? 'Anulada' : 'Aplicada'}
                                            </span>
                                        </td>
                                        <td className="text-end text-nowrap">
                                            <button type="button" className="btn btn-sm btn-outline-secondary" title="Ver detalle" onClick={() => openDetail(p)}>
                                                <i className="bi bi-eye" />
                                            </button>
                                            {p.status !== 'ANULADA' && (
                                                <button type="button" className="btn btn-sm btn-outline-danger ms-1" title="Anular compra" onClick={() => setCancelling(p)}>
                                                    <i className="bi bi-x-circle" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {data.totalPages > 1 && <Paginator totalPage={data.totalPages} pageActual={page} onChange={(p) => setPage(p)} />}
                </div>
            </div>

            {detail && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.4)' }} tabIndex={-1} role="dialog" aria-label="Detalle de la compra">
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Compra {detail.purchase.id}{detail.purchase.supplierInvoice ? ` · factura ${detail.purchase.supplierInvoice}` : ''}</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={() => setDetail(null)} />
                            </div>
                            <div className="modal-body table-responsive">
                                <table className="table table-sm align-middle">
                                    <thead>
                                        <tr className="text-muted small text-uppercase">
                                            <th>Producto</th>
                                            <th className="text-end">Cantidad</th>
                                            <th className="text-end">Costo neto</th>
                                            <th className="text-end">Valor</th>
                                            <th className="text-end">Otros costos</th>
                                            <th className="text-end">Costo final</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detail.lines.map((l, i) => (
                                            <tr key={i}>
                                                <td>{l.code} · {l.name}</td>
                                                <td className="text-end">{formatQuantity(l.quantity)}</td>
                                                <td className="text-end">{formatCost(l.unitCost, detail.purchase.currency)}</td>
                                                <td className="text-end">{formatMoney(l.value)}</td>
                                                <td className="text-end">{formatMoney(l.otherCosts)}</td>
                                                <td className="text-end fw-semibold">{formatCost(l.finalUnitCost)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <p className="text-muted small mb-0">Valores en la moneda del comercio. El costo neto está en la moneda de la factura.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {cancelling && (
                <Message
                    type="info"
                    title="Anular compra"
                    message={`Se revertirá el inventario de la compra ${cancelling.id} con el costo con que entró. Indique el motivo.`}
                    showReasonField={true}
                    reasonLabel="Motivo"
                    showCancel={true}
                    closeDialog={() => setCancelling(null)}
                    handleCancel={() => setCancelling(null)}
                    handleOk={cancel}
                    okText="Anular"
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}
            {error && <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />}
            {success && <Message type="success" title="Exitoso" message={success} closeDialog={() => setSuccess('')} handleOk={() => setSuccess('')} backgroundColor="rgba(0,0,0,0.4)" />}
        </div>
    );
};
